/**
 * Native Shoes — Anniversary Sale: client-side eligibility reveal.
 * ---------------------------------------------------------------------
 * WHY THIS EXISTS
 * The eligibility rule is a cookie (native_viewed_<tier-handle>),
 * and Liquid cannot read request cookies — nor would cookie-conditional
 * server markup be safe under Shopify's full-page cache even if it could.
 * So snippets/active-discount.liquid ALWAYS renders the discount price
 * and badge, hidden, tagged with data-nd-collection="<tier handle>", and
 * tags the untouched regular price with data-nd-regular. This script is
 * the only thing that ever unhides them.
 *
 * Eligibility is the sale-page cookie only (no logged-in or subscriber path).
 *
 * Runs on first paint and re-runs on every DOM mutation batch (debounced)
 * so cart-drawer re-renders, quick-add, infinite scroll and the collection
 * swatch grid all get covered without each needing its own hook.
 *
 * NO-LEAK DECODE (2026-10-06): snippets/active-discount-price.liquid and
 * snippets/active-discount-badge.liquid no longer render the real
 * discounted price/name/percent as plain (CSS-hidden) text — that was
 * still readable via "View Page Source". For an unproven visitor they
 * instead ship an EMPTY shell carrying only data-discount-payload, a
 * base64-encoded JSON blob (see snippets/active-discount.liquid's
 * ad_payload_b64). This script decodes that blob and BUILDS the visible
 * price/badge nodes itself, only after confirming the eligibility
 * cookie — so the real numbers never exist in the DOM at all until the
 * reveal actually happens.
 */
(function () {
  var COOKIE_PREFIX = 'native_viewed_';

  var cookieCache = null;

  function readCookies() {
    if (cookieCache && cookieCache.raw === document.cookie) return cookieCache.map;
    var map = {};
    var parts = document.cookie ? document.cookie.split(';') : [];
    for (var i = 0; i < parts.length; i++) {
      var pair = parts[i].trim();
      var eq = pair.indexOf('=');
      if (eq === -1) continue;
      map[pair.slice(0, eq)] = pair.slice(eq + 1);
    }
    cookieCache = { raw: document.cookie, map: map };
    return map;
  }

  function isEligible(handle) {
    if (!handle) return false;
    return readCookies()[COOKIE_PREFIX + handle] === '1';
  }

  // Decodes the base64 JSON payload emitted by active-discount.liquid's
  // ad_payload_b64. Tolerant: a missing/malformed attribute degrades to
  // "nothing to build" rather than throwing and blocking the whole
  // mutation-batch apply() pass.
  function decodePayload(el) {
    var raw = el.getAttribute('data-discount-payload');
    if (!raw) return null;
    try {
      var json = window.atob(raw);
      // decodeURIComponent/escape round-trip so multi-byte discount
      // names (accents, etc.) decode correctly from atob's binary string.
      try { json = decodeURIComponent(escape(json)); } catch (e2) {}
      return JSON.parse(json);
    } catch (e) {
      return null;
    }
  }

  // Builds the exact markup snippets/active-discount-price.liquid itself
  // emits when `visible` is true, so component-price.css's .price--on-sale
  // rules apply identically either way.
  function buildPriceMarkup(el, data) {
    el.innerHTML =
      '<div class="price__container">' +
        '<div class="price__regular">' +
          '<span class="price-item price-item--regular"></span>' +
        '</div>' +
        '<div class="price__sale">' +
          '<span aria-hidden="true"><s class="price-item price-item--regular"></s></span>' +
          '<span class="price-item price-item--sale price-item--last" aria-hidden="true"></span>' +
        '</div>' +
      '</div>';
    var regularItem = el.querySelector('.price__regular .price-item--regular');
    var compareItem = el.querySelector('.price__sale s.price-item--regular');
    var saleItem = el.querySelector('.price__sale .price-item--sale');
    if (regularItem) regularItem.textContent = data.price || '';
    if (compareItem) compareItem.textContent = data.compareAt || '';
    if (saleItem) saleItem.textContent = data.price || '';
    if (data.pct != null && data.compareAt && data.price) {
      el.setAttribute(
        'aria-label',
        'Regular price ' + data.compareAt + ', now ' + data.price + ', ' + data.pct + '% off.'
      );
    }
  }

  // Builds the exact markup active-discount-badge.liquid emits for
  // `visible: true`, for both the 'card' (bare span) and 'block'
  // (.price-sale-badge > .price-saving-badge) shapes.
  function buildBadgeMarkup(el, data) {
    var isBlock = el.classList.contains('price-sale-badge');
    var text = isBlock
      ? (data.badgeText || '')
      : (data.cardBadgeText || (typeof data.pct === 'number' ? data.pct + '% OFF' : ''));
    if (isBlock) {
      el.innerHTML = '<span class="price-saving-badge caption"></span>';
      var inner = el.querySelector('.price-saving-badge');
      if (inner) inner.textContent = text;
    } else {
      el.textContent = text;
    }
  }

  // Cart lines (mini cart + cart page): Shopify has not discounted the
  // line, so snippets/active-discount-line-badge.liquid ships a hidden
  // marker with the tier + original/sale money strings. Reveal = insert
  // the badge where the marker sits, and rewrite this row's unit price
  // ([data-nd-unit]) and line total ([data-nd-line]) into strikethrough
  // + sale price. Display only; checkout stays Shopify's real result.
  function money(cls) {
    return '<span class="visually-hidden">Regular price</span>' +
      '<s class="cart-item__old-price ' + cls + '"></s> ' +
      '<span class="visually-hidden">Sale price</span>' +
      '<span class="cart-item__final-price ' + cls + '"></span>';
  }

  function fillMoney(node, cls, oldText, newText) {
    node.innerHTML = money(cls);
    node.querySelector('s').textContent = oldText || '';
    node.querySelector('.cart-item__final-price').textContent = newText || '';
  }

  function buildCartLine(el, data) {
    var row = el.closest('tr.cart-item') || el.closest('.cart-item');
    if (!row) return;

    if (!row.querySelector('.cart-item__discount-badge')) {
      var badge = document.createElement('div');
      badge.className = 'price-sale-badge native-discount-badge cart-item__discount-badge';
      var label = document.createElement('span');
      label.className = 'price-saving-badge caption';
      label.textContent = data.badgeText || '';
      badge.appendChild(label);
      el.parentNode.insertBefore(badge, el.nextSibling);
    }

    var units = row.querySelectorAll('[data-nd-unit]');
    for (var i = 0; i < units.length; i++) {
      fillMoney(units[i], 'product-option', data.unitCompare, data.unitSale);
    }
    var lines = row.querySelectorAll('[data-nd-line]');
    for (var j = 0; j < lines.length; j++) {
      fillMoney(lines[j], 'price price--end', data.lineCompare, data.lineSale);
    }
  }

  function applyTo(el) {
    if (el.getAttribute('data-nd-applied') === '1') return;
    if (!isEligible(el.getAttribute('data-nd-collection'))) return;

    if (el.hasAttribute('data-nd-cartline')) {
      var lineData = decodePayload(el);
      if (lineData) buildCartLine(el, lineData);
      el.removeAttribute('data-discount-payload');
      el.setAttribute('data-nd-applied', '1');
      return;
    }

    // Build the real visible markup from the decoded payload BEFORE
    // unhiding — for an already-visible element there
    // is no payload attribute at all and this is a no-op.
    var data = decodePayload(el);
    if (data) {
      if (el.hasAttribute('data-nd-price')) buildPriceMarkup(el, data);
      else if (el.hasAttribute('data-nd-badge')) buildBadgeMarkup(el, data);
      el.removeAttribute('data-discount-payload');
    }

    el.setAttribute('data-nd-applied', '1');
    el.classList.remove('hidden');

    // Only a revealed PRICE block suppresses the regular price; a badge
    // on its own must never blank out the price next to it.
    if (!el.hasAttribute('data-nd-price')) return;

    var scope = el.closest('[data-nd-scope]') || el.parentElement;
    if (!scope) return;
    var regulars = scope.querySelectorAll('[data-nd-regular]');
    for (var i = 0; i < regulars.length; i++) regulars[i].classList.add('hidden');
  }

  // Cart summary (Total + free-shipping bar). snippets/active-discount-cart-
  // summary.liquid ships the preview total and the bar re-rendered for it.
  // Applied only once EVERY previewed line on the page has been revealed, so
  // the lines, the Total and the bar can never disagree.
  function allCartLinesApplied() {
    var lines = document.querySelectorAll('[data-nd-cartline]');
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].getAttribute('data-nd-applied') !== '1') return false;
    }
    return lines.length > 0;
  }

  function applyCartSummaries() {
    var markers = document.querySelectorAll('[data-nd-cartsummary]:not([data-nd-applied])');
    if (!markers.length || !allCartLinesApplied()) return;
    for (var i = 0; i < markers.length; i++) {
      var marker = markers[i];
      var data = decodePayload(marker);
      marker.removeAttribute('data-discount-payload');
      marker.setAttribute('data-nd-applied', '1');
      if (!data) continue;
      var scope = marker.parentElement;
      if (!scope) continue;
      var totals = scope.querySelectorAll('[data-nd-total]');
      for (var t = 0; t < totals.length; t++) totals[t].textContent = data.total || totals[t].textContent;
      var bar = scope.querySelector('free-shipping-progress');
      if (bar && data.fsp) bar.outerHTML = data.fsp;
    }
  }

  function apply(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-nd-collection]:not([data-nd-applied])');
    for (var i = 0; i < nodes.length; i++) applyTo(nodes[i]);
    applyCartSummaries();
  }

  // ── Reference sync: make checkout match what the shopper sees ────────────
  // WHY: everything above only changes what is DISPLAYED. Shopify prices the
  // cart and checkout from what is attached to the cart, and a segment-based
  // automatic discount never matches an anonymous visitor. So each tier has a
  // plain Shopify discount, and snippets/native-sale-ref.liquid hands us its
  // secret string (reversed + base64, in a Liquid-only collection metafield)
  // in a JSON island. When the shopper is eligible (sale-page cookie, or the
  // (cookie) we attach those strings to their cart
  // with Shopify's Ajax Cart API. Shopify then discounts each line for real,
  // for guests and logged-in customers alike, and the cart / checkout show
  // the right price. Read the Liquid snippet's header for the full rationale.
  //
  // Notes
  // - The Ajax Cart API's parameter name is fixed by Shopify ('discount' on
  //   /cart/update.js, 'discount_codes' on /cart.js); those two literals are
  //   the only places the plain word appears. Everything else here is named
  //   refs/tokens on purpose.
  // - A cart holds these across later add-to-bag calls, and it can hold them
  //   while EMPTY, so applying on the sale-page visit is enough; items added
  //   afterwards are discounted by tier.
  // - We merge with what the cart already has, so a shopper's own entered
  //   string is never removed.
  // - It is a two-way reconcile: tiers the shopper is eligible for are attached, tiers they are
  //   NOT (cookie cleared, window closed) are removed from the cart, before an add goes out.
  // - sessionStorage keeps one opaque hash of "cart token + eligible set" so a normal page view
  //   costs nothing. A new cart token or a change in eligibility triggers one reconcile.
  var REF_MARK = 'nd_s';

  function routeRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function readRefTiers() {
    var node = document.querySelector('script[data-nd-ref]');
    if (!node) return [];
    try { return (JSON.parse(node.textContent) || {}).r || []; } catch (e) { return []; }
  }

  // stored value = base64(reverse(string)); undo both. Never written to the DOM.
  function unpackRef(v) {
    try { return window.atob(v).split('').reverse().join(''); } catch (e) { return ''; }
  }

  // Every live tier's string, eligible or not: what we are allowed to add AND to remove.
  function allTokens() {
    var tiers = readRefTiers();
    var out = [];
    for (var i = 0; i < tiers.length; i++) {
      var v = tiers[i] && tiers[i].v ? unpackRef(tiers[i].v) : '';
      if (v) out.push(v);
    }
    return out;
  }

  // The tiers this shopper may have right now (the sale-page cookie,
  // inside the tier's window). Cleared cookie => not eligible => their strings come off the cart.
  function eligibleTokens() {
    var nowSec = Math.floor(Date.now() / 1000);
    var tiers = readRefTiers();
    var out = [];
    for (var i = 0; i < tiers.length; i++) {
      var t = tiers[i];
      if (!t || !t.v) continue;
      if (t.a && nowSec < t.a) continue;
      if (t.z && nowSec > t.z) continue;
      if (t.k === 0) continue;                 // this shopper's country is not in the tier's markets list
      if (isEligible(t.h)) out.push({ h: t.h, v: unpackRef(t.v) });
    }
    return out.filter(function (x) { return x.v; });
  }

  function cartTokenFromCookie() {
    var m = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
    return m ? decodeURIComponent(m[1]).split('?')[0] : '';
  }

  // sessionStorage holds ONE opaque value: a 32-bit hash of (cart token + the eligible set).
  // It tells us "this cart was already reconciled for this exact state" so a normal page view costs
  // nothing. It carries no tier names, no cart token and nothing readable: just an unlabeled hash.
  function fingerprint(text) {
    var h = 2166136261;
    for (var i = 0; i < text.length; i++) { h ^= text.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
    return h.toString(36);
  }
  function markFor(token, want) {
    return fingerprint(token + '\u0001' + want.map(function (w) { return w.h; }).sort().join(','));
  }
  function readMark() { try { return sessionStorage.getItem(REF_MARK) || ''; } catch (e) { return ''; } }
  function writeMark(v) { try { sessionStorage.setItem(REF_MARK, v); } catch (e) {} }

  // Tiny diagnostic trace: counts and times only (never strings, tier names or tokens), kept in
  // sessionStorage (cap 40) so a problem that spans several page loads can be read back with
  // NativeAnniversarySale.trace(). k: r = reconcile (have/want/next counts), m = strings the cart
  // did not keep, c = checkout guard ran, x = request failed.
  function trace(k, a, b, c) {
    try {
      var list = JSON.parse(sessionStorage.getItem('nd_t') || '[]');
      list.push([Math.floor(Date.now() / 1000) % 100000, k, a, b, c]);
      sessionStorage.setItem('nd_t', JSON.stringify(list.slice(-40)));
    } catch (e) {}
  }

  // True when there is nothing to do: no cart and nothing to attach, or this cart was already
  // reconciled for exactly this eligibility.
  function inSync(want) {
    var token = cartTokenFromCookie();
    if (!token) return !want.length;
    return readMark() === markFor(token, want);
  }

  // Staff paste the code by hand, and Shopify treats discount codes as case-insensitive, so every
  // comparison between our codes and the cart's is case-insensitive and ignores stray spaces.
  function norm(v) { return String(v == null ? '' : v).trim().toLowerCase(); }
  function hasCode(list, v) { var n = norm(v); for (var i = 0; i < list.length; i++) { if (norm(list[i]) === n) return true; } return false; }

  var syncing = null;

  // Make the cart's attached strings match eligibility: attach the eligible tiers', REMOVE the
  // others' (keeping any string the shopper entered themselves). Resolves true when the cart was
  // changed, false otherwise. Never throws.
  function syncRefs(force) {
    if (syncing) return syncing;
    if (!readRefTiers().length) return Promise.resolve(false);   // sale not running: nothing to manage
    var want = eligibleTokens();
    if (!force && inSync(want)) return Promise.resolve(false);   // force = re-verify against the real cart

    var root = routeRoot();
    var ours = allTokens();
    var wantVals = want.map(function (w) { return w.v; });
    syncing = fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var have = (cart.discount_codes || []).map(function (c) { return c.code; });
        var keep = have.filter(function (v) { return !hasCode(ours, v) || hasCode(wantVals, v); });
        var next = keep.concat(wantVals.filter(function (v) { return !hasCode(keep, v); }));
        var same = next.length === have.length && next.every(function (v) { return hasCode(have, v); });
        var token = cart.token ? String(cart.token).split('?')[0] : cartTokenFromCookie();
        trace('r', have.length, wantVals.length, next.length);
        if (same) { writeMark(markFor(token, want)); return false; }
        return fetch(root + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: next.join(',') })   // '' removes them all
        }).then(function (r) { return r.json(); }).then(function (c) {
          // Verify Shopify kept every string we asked for; if not, try the missing ones once more.
          var got = ((c && c.discount_codes) || []).map(function (x) { return x.code; });
          var missing = next.filter(function (v) { return !hasCode(got, v); });
          if (!missing.length) {
            writeMark(markFor((c && c.token ? String(c.token).split('?')[0] : '') || token, want));
            return true;
          }
          return fetch(root + 'cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: got.concat(missing).join(',') })
          }).then(function (r) { return r.json(); }).then(function (c2) {
            var got2 = ((c2 && c2.discount_codes) || []).map(function (x) { return x.code; });
            var still = next.filter(function (v) { return !hasCode(got2, v); });
            trace('m', missing.length, still.length);
            try { if (still.length) console.info('[nd] ' + still.length + ' reference(s) not accepted by the cart'); } catch (e) {}
            // only remember "in sync" when everything stuck, so the next check tries again
            if (!still.length) writeMark(markFor((c2 && c2.token ? String(c2.token).split('?')[0] : '') || token, want));
            return true;
          });
        });
      })
      .catch(function () { trace('x'); return false; })
      .then(function (changed) { syncing = null; return changed; });
    return syncing;
  }

  // Reconcile BEFORE an add-to-bag goes out, so the add is priced for the shopper's current
  // eligibility (cleared cookie => discount already off the cart; first add => strings already on
  // it). Costs one round trip only when the cart is out of sync. Also reconcile after a successful
  // add in case it created a brand-new cart.
  (function watchAdds() {
    if (!window.fetch || window.__ndAddWatch) return;
    window.__ndAddWatch = true;
    var native = window.fetch;
    var noop = function () {};
    window.fetch = function (input) {
      var self = this, args = arguments, url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      if (!/\/cart\/add(\.js)?(\?|$)/.test(url)) return native.apply(self, args);

      var pre = Promise.resolve(false);
      try { if (readRefTiers().length && !inSync(eligibleTokens())) pre = syncRefs(); } catch (e) {}
      return pre.then(noop, noop).then(function () {
        var p = native.apply(self, args);
        p.then(function (res) { if (res && res.ok) setTimeout(syncRefs, 0); }, noop);
        return p;
      });
    };
  })();

  // Last-moment guard: right before the shopper leaves for checkout, re-verify the cart against
  // what they are eligible for and re-attach anything missing, so checkout can never be behind
  // what the cart showed. Works for the cart page and the mini cart (both use a submit button named
  // "checkout"). Costs one GET when everything is already attached; the click then continues as normal.
  (function guardCheckout() {
    var passing = false;
    document.addEventListener('click', function (e) {
      if (passing) return;
      var btn = e.target && e.target.closest ? e.target.closest('button[name="checkout"], input[name="checkout"]') : null;
      if (!btn || btn.disabled || !btn.form || !readRefTiers().length) return;
      e.preventDefault();
      e.stopPropagation();
      var go = function () {
        passing = true;
        try { btn.form.requestSubmit(btn); } catch (err) { btn.form.submit(); }
      };
      trace('c');
      var timer = setTimeout(go, 4000);              // never trap the shopper on a slow network
      syncRefs(true).then(function () { clearTimeout(timer); go(); }, function () { clearTimeout(timer); go(); });
    }, true);
  })();

  // Public hook so assets/native-collection-swatches.js can re-apply
  // immediately after it rebuilds a card's badge row / price block,
  // instead of waiting on the mutation observer's debounce.
  window.NativeAnniversarySale = {
    apply: apply,
    sync: syncRefs,
    trace: function () { try { return JSON.parse(sessionStorage.getItem('nd_t') || '[]'); } catch (e) { return []; } },
    isEligible: isEligible
  };

  function boot() {
    apply(document);
    syncRefs();

    if (!window.MutationObserver) return;
    var pending = null;
    // Feature 311: only re-run apply() when the added nodes are, or contain, sale markup.
    // Re-scanning the whole page for every third-party or theme DOM change cost 70-120 ms
    // per load on pages with no sale nodes. apply() itself still scans the whole document,
    // so anything left unrevealed earlier is picked up on the next relevant addition.
    var SALE_SEL = '[data-nd-collection],[data-nd-cartsummary],[data-nd-cartline]';
    function addedSaleMarkup(records) {
      for (var r = 0; r < records.length; r++) {
        var added = records[r].addedNodes;
        for (var a = 0; a < added.length; a++) {
          var n = added[a];
          if (n.nodeType !== 1) continue;
          if ((n.matches && n.matches(SALE_SEL)) || (n.querySelector && n.querySelector(SALE_SEL))) return true;
        }
      }
      return false;
    }
    var observer = new MutationObserver(function (records) {
      if (pending) return;
      if (!addedSaleMarkup(records)) return;
      pending = window.requestAnimationFrame(function () {
        pending = null;
        apply(document);
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
